import React from 'react';
import { Platform } from 'react-native';
import { useTranslation } from 'react-i18next';
import { VStack } from '@/components/gluestack/vstack';
import { Text } from '@/components/gluestack/text';
import { Divider } from '@/components/gluestack/divider';
import { Button, ButtonText } from '@/components/gluestack/button';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import { LinkSquare02Icon } from '@hugeicons-pro/core-stroke-rounded';
import { SUPPORT_URLS } from '@/constants/urls';
import { useOpenWebPage } from '@/hooks/useOpenWebPage';
import { useSupportService } from '@/hooks/useSupportService';
import { useIAPContext } from '@/components/providers/IAPProvider';

interface IStorePlansTermsProps {
  /** Currency the store prices are shown in */
  currency: string;
}

/**
 * App Store / Google Play subscription terms with billing support, restore purchases and legal links
 * (no card chrome). Reads its handlers from the IAP context so callers can pass it around as a
 * stable element (e.g. into the right panel).
 */
export function StorePlansTerms({ currency }: IStorePlansTermsProps) {
  const { t } = useTranslation();
  const openWebPage = useOpenWebPage();
  const { submitBillingSupport } = useSupportService();
  const { isProcessing, handleRestorePurchases } = useIAPContext();

  const links = [
    { key: 'support', label: t('account.plans.getBillingSupport'), onPress: submitBillingSupport },
    {
      key: 'restore',
      label: t('account.plans.restorePurchases'),
      onPress: handleRestorePurchases,
      disabled: isProcessing,
    },
    {
      key: 'privacy',
      label: t('account.plans.privacyPolicy'),
      onPress: () => openWebPage(SUPPORT_URLS.atlassian.legal.privacyPolicy),
    },
    {
      key: 'terms',
      label: t('account.plans.termsOfUse'),
      onPress: () => openWebPage(SUPPORT_URLS.atlassian.legal.termsOfUse),
    },
  ];

  return (
    <VStack space="md">
      <Text size="xs" className="text-typography-600">
        {t('account.plans.pricesIn', { currency })}
      </Text>

      <Text size="xs" className="leading-5 text-typography-600">
        {t(
          Platform.OS === 'ios'
            ? 'account.plans.billingTerms_ios'
            : Platform.OS === 'android'
              ? 'account.plans.billingTerms_android'
              : 'account.plans.billingTerms_web'
        )}
      </Text>

      <Divider />

      <VStack space="xs">
        {links.map((link) => (
          <Button
            key={link.key}
            variant="link"
            action="primary"
            onPress={link.onPress}
            disabled={link.disabled}
            className="self-start">
            <HugeIconsWrapper icon={LinkSquare02Icon} size={18} className="color-primary-500" />
            <ButtonText className="text-primary-500">{link.label}</ButtonText>
          </Button>
        ))}
      </VStack>
    </VStack>
  );
}
